<template>
    <div class="search-engine">
        <div class="search-engine-container">
            <span v-if="from" class="search-engine-from-title">{{ $t('search-engine.from-title', { airport: from.name }) }}</span>
            <div class="search-engine-switch-container">
                <div :class="['segment-switch', tripType]">
                    <button :class="{ active: tripType === 'round' }" @click="tripType = 'round'">
                        {{ $t('search-engine.trip-types.round') }}
                    </button>
                    <button :class="{ active: tripType === 'one-way' }" @click="tripType = 'one-way'">
                        {{ $t('search-engine.trip-types.oneway') }}
                    </button>
                    <button :class="{ active: tripType === 'multi' }" @click="goToResults">
                        {{ $t('search-engine.trip-types.multi') }}
                    </button>
                </div>
            </div>
            <div class="search-engine-fields">
                <div class="search-engine-fields-container">
                    <div class="search-engine-fields-container-trip">
                        <div class="search-engine-fields-container-trip-from">
                            <div class="input-wrapper" :class="{ focused: isFromFocused || tripFrom }">
                                <label>De</label>
                                <input type="text" class="search-engine-fields-container-trip-from-input"
                                       v-model="tripFrom" @focus="isFromFocused = true; showSuggestionsFrom = true"
                                       @blur="isFromFocused = false" @input="onInputFrom"
                                >
                                <i class="search-icon" />
                            </div>
                            <ul v-if="showSuggestionsFrom && matchingAirports.length" class="suggestions-list">
                                <li v-for="airport in matchingAirports" :key="airport.id"
                                    @click="selectFromAirport(airport)"
                                >
                                    <img class="flag" :src="getFlag(airport.city)">
                                    <span>{{ airport.full_name }} ({{ airport.OACI }}),</span>
                                    <small>
                                        {{ getAdminLevels(airport.city) }}
                                    </small>
                                </li>
                            </ul>
                        </div>
                        <div class="search-engine-fields-container-trip-to">
                            <div class="input-wrapper" :class="{ focused: isToFocused || tripTo }">
                                <label>À</label>
                                <input type="text" class="search-engine-fields-container-trip-to-input" v-model="tripTo"
                                       @focus="isToFocused = true; showSuggestionsTo = true" @blur="isToFocused = false"
                                       @input="onInputTo"
                                >
                                <i class="search-icon" />
                            </div>
                            <ul v-if="showSuggestionsTo && matchingAirports.length" class="suggestions-list">
                                <li v-for="airport in matchingAirports" :key="airport.id"
                                    @click="selectToAirport(airport)"
                                >
                                    <img class="flag" :src="getFlag(airport.city)">
                                    <span>{{ airport.full_name }} ({{ airport.OACI }}),</span>
                                    <small>
                                        {{ getAdminLevels(airport.city) }}
                                    </small>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="search-engine-fields-container-date" :class="{ 'one-way': tripType === 'one-way' }">
                        <div class="search-engine-fields-container-date-departure flex-col sm:flex-row" :class="{ '!flex-row': tripType === 'one-way' }">
                            <div class="search-engine-fields-container-date-departure-title" @click.stop="focusGonePicker">
                                <img src="/webapp_assets/icons/calendar_green.svg" alt="Calendar Icon">
                                <span>{{ $t('search-engine.outward') }}</span>
                            </div>
                            <div class="search-engine-fields-container-date-departure-date" :class="{ '!flex-row !items-center': tripType === 'one-way' }">
                                <div class="date-picker grow" :class="{ '!mt-0 !ml-2': tripType === 'one-way' }">
                                    <DateTimePicker ref="gonePicker" v-model="goneDate" :display-time-value="goneTime" @input-time="goneTime = $event" />
                                </div>
                                <div class="hour" :class="{ '!mt-0': tripType === 'one-way' }" @click.stop="focusGonePicker">
                                    {{ goneTime }}
                                </div>
                            </div>
                        </div>
                        <div class="search-engine-fields-container-date-departure flex-col sm:flex-row" v-if="tripType === 'round'">
                            <div class="search-engine-fields-container-date-departure-title" @click.stop="focusReturnPicker">
                                <img src="/webapp_assets/icons/calendar_green.svg" alt="Calendar Icon">
                                <span>{{ $t('search-engine.return') }}</span>
                            </div>
                            <div class="search-engine-fields-container-date-departure-date">
                                <div class="date-picker grow">
                                    <DateTimePicker ref="returnPicker" v-model="returnDate" :display-time-value="returnTime" :min-date="goneDate" @input-time="returnTime = $event" />
                                </div>
                                <div class="hour" @click.stop="focusReturnPicker">
                                    {{ returnTime }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="search-engine-fields-container-passengers">
                        <div class="search-engine-fields-container-passengers-img">
                            <img src="/webapp_assets/icons/passenger_green.svg" alt="Passenger Icon">
                            <span>{{ $t('search-engine.passengers') }}</span>
                        </div>
                        <div class="search-engine-fields-container-passengers-controls">
                            <button @click="nbPassengers--" :disabled="nbPassengers < 2">
                                -
                            </button>
                            <span>{{ nbPassengers }}</span>
                            <button @click="nbPassengers++">
                                +
                            </button>
                        </div>
                    </div>
                </div>
                <div class="search-engine-fields-button">
                    <button class="search-engine-fields-button-submit" :disabled="!canSubmit" @click="onSubmit">
                        <img src="/webapp_assets/icons/search.svg" alt="Search Icon">
                        {{ $t('search-engine.search') }}
                    </button>
                </div>
            </div>
            <p class="search-engine-disclaimer">{{ $t('search-engine.estimates-disclaimer') }}</p>
        </div>
    </div>
</template>
<script lang="js">
import { get } from 'lodash';
import DateTimePicker from './date-time-picker/DateTimePicker.vue';
export default {
    name: 'SearchEngine',
    components: {
        DateTimePicker,
    },
    props: {
        referal: {
            type: String,
            default: ''
        },
        domain: {
            type: String,
            default: 'https://openfly.fr'
        },
        from: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            tripType: 'round',
            nbPassengers: 1,
            tripFrom: '',
            tripTo: '',
            tripFromId: null,
            tripToId: null,
            // Data for airports and cities
            nearCity: null,
            matchingAirportsNearCity: [],
            matchingAirports: [],
            matchingCities: [],
            showSuggestionsFrom: false,
            showSuggestionsTo: false,
            isFromFocused: false,
            isToFocused: false,
            goneDate: (() => {
                const d = new Date();
                d.setDate(d.getDate() + 7);
                return d;
            })(),
            goneTime: '09:00',
            returnDate: (() => {
                const d = new Date();
                d.setDate(d.getDate() + 7);
                return d;
            })(),
            returnTime: '20:00',
        };
    },
    computed: {
        canSubmit() {
            return this.tripFrom && this.tripTo;
        }
    },
    watch: {
        goneDate() {
            this.updateReturnFromGone();
        },
        goneTime() {
            this.updateReturnFromGone();
        },
    },
    created() {
        //
    },
    mounted() {
        // Any initialization logic can go here
        document.addEventListener('click', this.handleClickOutside);
        if (!!this.from) {
            this.selectFromAirport(this.from);
        }
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        goToResults() {
            const params = new URLSearchParams({
                referal: this.referal,
                tripType: 'multi',
            });
            const parentDomain = this.getParentDomain();
            if (parentDomain) {
                params.set('parent_domain', parentDomain);
            }
            window.open(`${this.domain}/search-engine-results?${params.toString()}`);
        },
        updateReturnFromGone() {
            if (this.tripType !== 'round' || !this.goneDate || !this.goneTime) {
                return;
            }
            const base = new Date(this.goneDate);
            const [h, m] = String(this.goneTime).split(':');
            base.setHours(parseInt(h || '0'));
            base.setMinutes(parseInt(m || '0'));
            base.setSeconds(0);
            base.setMilliseconds(0);
            const plus12 = new Date(base.getTime() + 12 * 60 * 60 * 1000);
            this.returnDate = plus12;
            this.returnTime = this.handleInput(plus12);
        },
        clearData() {
            this.matchingAirports = [];
            this.nearCity = null;
            this.matchingCities = [];
            this.matchingAirportsNearCity = [];
        },
        async fetchAirportsAndCities(value) {
            this.clearData();

            const airportsPromise = this.fetchAirports({ term: value });
            airportsPromise.then((airports) => this.matchingAirports = airports.data);
            const citiesPromise = this.fetchCities({ term: value });
            citiesPromise.then((cities) => this.matchingCities = cities.data);

            await Promise.all([airportsPromise, citiesPromise]);
        },
        async fetchAirports(options) {
            const airports = await axios.get('/api/airports', {
                params: {
                    locale: 'fr',
                    per_page: 5,
                    disable_pagination: 1,
                    ...options,
                },
            });

            return airports;
        },
        async fetchCities(options) {
            const cities = await axios.get('/api/cities', {
                params: {
                    locale: 'fr',
                    per_page: 5,
                    disable_pagination: 1,
                    with_country: 1,
                    with_admin_levels: 1,
                    ...options,
                },
            });
            return cities;
        },
        onInputFrom() {
            this.fetchAirportsAndCities(this.tripFrom);
            this.showSuggestionsFrom = true;
        },
        selectFromAirport(airport) {
            this.tripFrom = `${airport.name} (${airport.OACI})`;
            this.tripFromId = airport.id;
            this.showSuggestionsFrom = false;
        },
        onInputTo() {
            this.fetchAirportsAndCities(this.tripTo);
            this.showSuggestionsTo = true;
        },
        selectToAirport(airport) {
            this.tripTo = `${airport.name} (${airport.OACI})`;
            this.tripToId = airport.id;
            this.showSuggestionsTo = false;
        },
        getAdminLevels(city) {
            const lvl = city.admin_level2 ?
                city.admin_level2.name :
                (city.admin_level1 ?
                    city.admin_level1.name
                    : null);

            let txt = lvl ? lvl : '';

            if (city.country) {
                txt += lvl ? `, ${city.country.name}` : city.country.name;
            } else {
                txt += city.admin_level1 ? `, ${city.admin_level1.name}` : '';
            }

            return txt;
        },
        getFlag(city) {
            const iso = get(city, 'country.isoCode');
            return iso ? `/imgs/flags/${iso.toLowerCase()}.webp` : null;
        },
        handleClickOutside(event) {
            const fromInput = this.$el.querySelector('.search-engine-fields-container-trip-from .input-wrapper');
            const toInput = this.$el.querySelector('.search-engine-fields-container-trip-to .input-wrapper');

            if (fromInput && !fromInput.contains(event.target)) {
                this.showSuggestionsFrom = false;
            }
            if (toInput && !toInput.contains(event.target)) {
                this.showSuggestionsTo = false;
            }
            this.clearData();
        },
        handleInput(dt) {
            const pad = n => (n < 10 ? '0' + n : n);
            return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        },
        onSubmit() {
            // Fusionner goneDate et goneTime en goneDateTime
            const goneDateTime = new Date(this.goneDate);
            const [goneHour, goneMinute] = this.goneTime.split(':');
            goneDateTime.setHours(parseInt(goneHour));
            goneDateTime.setMinutes(parseInt(goneMinute));

            // Fusionner returnDate et returnTime en returnDateTime si round
            let returnDateTime = null;
            if (this.tripType === 'round') {
                returnDateTime = new Date(this.returnDate);
                const [returnHour, returnMinute] = this.returnTime.split(':');
                returnDateTime.setHours(parseInt(returnHour));
                returnDateTime.setMinutes(parseInt(returnMinute));
            }

            const params = new URLSearchParams({
                referal: this.referal,
                goneDate: goneDateTime.toISOString(),
                returnDate: returnDateTime ? returnDateTime.toISOString() : '',
                tripType: this.tripType,
                from: this.tripFrom,
                to: this.tripTo,
                fromId: this.tripFromId,
                toId: this.tripToId,
                nbPassengers: this.nbPassengers,
            });
            // Pass parent domain for tracking when embedded in iframe
            const parentDomain = this.getParentDomain();
            if (parentDomain) {
                params.set('parent_domain', parentDomain);
            }
            // Set cookie for the current domain only (host-only cookie)
            window.open(`${this.domain}/search-engine-results?${params.toString()}`);
        },
        focusGonePicker() {
            this.$refs.gonePicker.openCalendar();
        },
        focusReturnPicker() {
            this.$refs.returnPicker.openCalendar();
        },
        getParentDomain() {
            try {
                if (document.referrer) {
                    const url = new URL(document.referrer);
                    return url.host;
                }
            } catch (e) {
                // Cross-origin restriction
            }
            return null;
        }
    }
};
</script>
<style lang="scss" scoped>
.search-engine {
    max-width: 1280px;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #2B3B61;
    padding-left: 20px;
    padding-right: 20px;

    .search-engine-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 10px;
        background-color: #2c3b60b5;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        max-width: 1280px;
        width: 100%;

        .search-engine-from-title {
            font-size: 20px;
            color: white;
            margin-bottom: 10px;
            margin-top: 10px;
            font-weight: bold;
        }

        .search-engine-switch-container {
            margin-bottom: 10px;

            .segment-switch {
                position: relative;
                display: flex;
                background-color: rgba(255, 255, 255, 0.31);
                border-radius: 10px;
                overflow: hidden;
                box-shadow: 0 0 0 1px #2c3b6047;
                padding: 2px;

                &::before {
                    content: '';
                    position: absolute;
                    top: 2px;
                    bottom: 2px;
                    left: 2px;
                    width: calc(34% - 2px);
                    background-color: white;
                    border-radius: 8px;
                    transition: transform 0.3s ease;
                    z-index: 0;
                    transform: translateX(0%);
                }

                &.one-way::before {
                    transform: translateX(100%);
                }

                button {
                    font-size: 12px;
                    padding: 6px 16px;
                    border: none;
                    background-color: transparent;
                    color: white;
                    cursor: pointer;
                    font-weight: 500;
                    z-index: 1;
                    position: relative;
                }

                .active {
                    color: #2A3B60;
                }
            }
        }

        .search-engine-disclaimer {
            font-size: 11px;
            color: rgba(255, 255, 255, 0.7);
            text-align: center;
            margin-top: 8px;
            margin-bottom: 0;
            @media screen and (min-width: 1023px) {
                padding-left: 10px;
            }
        }

        .search-engine-fields {
            display: flex;
            width: 100%;
            min-height: 70px;

            @media screen and (max-width: 1023px) {
                flex-direction: column;
            }

            .search-engine-fields-container {
                flex-grow: 1;
                display: flex;
                border-radius: 15px;
                margin-right: 10px;

                @media screen and (max-width: 1023px) {
                    flex-direction: column;
                    margin-right: 0;

                    >:last-child {
                        margin-bottom: 10px;
                    }
                }

                .search-engine-fields-container-trip {
                    min-height: 60px;
                    padding: 10px;
                    display: flex;
                    border-right: #D5D5D5 1px solid;
                    flex-grow: 1;
                    background-color: white;
                    border-top-left-radius: 15px;
                    border-bottom-left-radius: 15px;

                    @media screen and (max-width: 1023px) {
                        min-height: 120px;
                        border-radius: 15px;
                        margin-bottom: 10px;
                        flex-direction: column;
                    }

                    .search-engine-fields-container-trip-from {
                        // Styles for the "from" field
                        flex-basis: 50%;
                        display: flex;
                        padding: 0 12px;
                        position: relative;

                        @media screen and (max-width: 1023px) {
                            margin: 5px 0;
                            flex-grow: 1;
                        }
                    }

                    .search-engine-fields-container-trip-to {
                        // Styles for the "to" field
                        flex-basis: 50%;
                        display: flex;
                        padding: 0 12px;
                        position: relative;
                        
                        @media screen and (max-width: 1023px) {
                            margin: 5px 0;
                            flex-grow: 1;
                        }
                    }

                    .input-wrapper {
                        position: relative;
                        display: flex;
                        align-items: center;
                        width: 100%;

                        label {
                            position: absolute;
                            left: 0;
                            top: 50%;
                            transform: translateY(-50%);
                            font-size: 14px;
                            color: #2B3B61;
                            pointer-events: none;
                            transition: 0.2s ease all;
                            z-index: 3;
                        }

                        input {
                            width: 100%;
                            border: none;
                            border-bottom: 1px solid #ccc;
                            padding-left: 0;
                            padding-right: 20px;
                            border-radius: 0;
                            &:focus {
                                outline: none;
                            }
                        }

                        .search-icon {
                            position: absolute;
                            right: 0;
                            width: 16px;
                            height: 16px;
                            background-image: url('/webapp_assets/icons/search_secondary.svg');
                            background-repeat: no-repeat;
                            background-size: contain;
                            z-index: 2;
                        }

                        &.focused label {
                            font-size: 10px;
                            top: 2px;
                        }
                    }

                    .suggestions-list {
                        position: absolute;
                        z-index: 10;
                        background: white;
                        list-style: none;
                        margin: 0;
                        padding: 5px;
                        border: 1px solid #ddd;
                        max-height: 200px;
                        overflow-y: auto;
                        width: max-content;
                        top: 35px;
                        border-radius: 10px;

                        li {
                            display: flex;
                            align-items: center;
                            padding: 6px 10px;
                            cursor: pointer;

                            &:hover {
                                background-color: #f5f5f5;
                            }

                            .flag {
                                width: 23px;
                                height: 14px;
                                border: solid 1px #E2E2E2;
                                position: relative;
                                vertical-align: middle;
                                bottom: 2px;
                            }

                            span {
                                margin-left: 10px;
                                font-size: 14px;
                                font-weight: 500;
                            }
                        }
                    }
                }

                .search-engine-fields-container-date {
                    display: flex;
                    flex-direction: column;
                    border-right: #D5D5D5 1px solid;
                    background-color: white;

                    > :first-child {
                        border-bottom: #D5D5D5 1px solid;
                    }

                    @media screen and (max-width: 1023px) {
                        flex-direction: row;
                        border-radius: 15px;
                        margin-bottom: 10px;
                        padding: 0px 10px;

                        > :first-child {
                            border-bottom: none;
                            border-right: #D5D5D5 1px solid;
                        }
                    }


                    .search-engine-fields-container-date-departure {
                        display: flex;
                        flex-basis: 50%;
                        padding: 6px;
                        justify-content: space-between;
                        height: 100%;
                        cursor: pointer;

                        .search-engine-fields-container-date-departure-title {
                            display: flex;
                            align-items: center;

                            img {
                                width: 14px;
                                height: 14px;
                                margin-right: 6px;
                            }

                            >span {
                                font-size: 10px;
                                padding-top: 3px;
                            }
                        }

                        .search-engine-fields-container-date-departure-date {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;


                            >div {
                                padding: 0 6px;
                                font-size: 12px;
                                font-weight: 600;
                                position: relative;
                                top: 2px;
                            }

                            > :first-child {
                                border-right: #D5D5D5 1px solid;
                            }

                            .hour {
                                display: flex;
                                justify-content: center;
                                min-width: 0;
                                width: 52px;
                                padding: 0;
                                margin: 0;
                                margin-left: 6px;
                                font-size: 12px;
                                font-weight: 400;
                            }

                        }
                    }
                }

                .search-engine-fields-container-date.one-way {
                    // Styles for the one-way trip
                    justify-content: center;
                    background-color: white;

                    > :first-child {
                        border-bottom: none;

                        @media screen and (max-width: 1023px) {
                            border-right: none;

                        }
                    }

                    .search-engine-fields-container-date-departure {
                        flex-basis: auto;

                        .search-engine-fields-container-date-departure-title {
                            justify-content: center;
                        }

                        .search-engine-fields-container-date-departure-date {
                            justify-content: center;
                        }
                    }
                }

                .search-engine-fields-container-passengers {
                    // Styles for the passengers field
                    display: flex;
                    align-items: center;
                    justify-content: space-evenly;
                    padding: 0 12px;
                    margin-right: 6px;
                    flex-direction: column;
                    background-color: white;
                    border-top-right-radius: 15px;
                    border-bottom-right-radius: 15px;

                    @media screen and (max-width: 1023px) {
                        flex-direction: row;
                        border-radius: 15px;
                        margin-right: 0;
                        padding: 6px 14px;
                        justify-content: space-between;
                    }

                    .search-engine-fields-container-passengers-img {
                        display: flex;
                        align-items: center;

                        span {
                            font-size: 12px;
                            font-weight: 400;
                            margin-left: 6px;
                            margin-top: 2px;

                            @media screen and (min-width: 1023px) {
                                display: none;
                            }
                        }
                    }

                    .search-engine-fields-container-passengers-controls {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 16px;

                        button {
                            background-color: transparent;
                            color: #2B3B61;
                            font-size: 16px;
                            font-weight: 600;
                            cursor: pointer;
                            border: 1px solid #1EA07F;
                            border-radius: 4px;
                            width: 24px;
                            height: 24px;
                        }

                        :disabled {
                            cursor: not-allowed;
                            background-color: #F1F1F1;
                            border: none;
                        }

                        span {
                            text-align: center;
                            font-size: 12px;
                            font-weight: 600;
                            margin: 0 4px;
                            width: 20px;
                        }
                    }
                }
            }

            .search-engine-fields-button {
                display: flex;

                @media screen and (max-width: 1023px) {
                    justify-content: center;
                }

                .search-engine-fields-button-submit {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-direction: column;
                    // Styles for the search button
                    padding: 0 12px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    color: white;
                    height: 100%;
                    border-radius: 15px;
                    font-weight: bold;
                    font-size: 12px;
                    background-color: #EA5354;
                    cursor: pointer;

                    @media screen and (max-width: 1023px) {
                        flex-direction: row;
                        padding: 6px 30px;
                    }

                    img {
                        width: 20px;
                        height: 20px;
                        margin-bottom: 4px;

                        @media screen and (max-width: 1023px) {
                            margin-bottom: 0px;
                            margin-right: 6px;
                        }
                    }
                }

                .search-engine-fields-button-submit:disabled {
                    cursor: not-allowed;
                    background-color: rgba(255, 255, 255, 0.31);
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/SearchEngine.vue